// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
// import { hot } from 'react-hot-loader';
import { GlobalStyles } from "tss-react";
import { withStyles } from "tss-react/mui";
import { Typography } from '@mui/material';
import { Route, withRouter, RouteComponentProps } from 'react-router-dom';
import { observer } from 'mobx-react';
import { makeObservable, observable, runInAction } from 'mobx';

import PrivateModule from '@modules/Private';
import PublicModule from '@modules/Public';
import container from '@core/di';
import AuthStore from '@core/stores/auth';
import ErrorHandler from '@shared/components/ErrorHandler';
import themev5, { scrollStyles } from '@core/themev5';

export interface AppProps extends RouteComponentProps {
  className?: string;
}

// Main entry point.
@observer
class App extends React.Component<AppProps> {
  private authStore = container.get<AuthStore>(AuthStore.diToken);
  @observable appCrashed: boolean = false;

  constructor(props: AppProps) {
    super(props);
    makeObservable(this);
  }

  // Catch-all and raise flag.
  componentDidCatch() {
    runInAction(() => {
      this.appCrashed = true;  
    });
  }

  render() {
    const classes = withStyles.getClasses(this.props);

    // Show crashed page.
    if (this.appCrashed) {
      return <ErrorHandler />;
    }

    // Get login status and route to either the private or public.
    const { loggedIn } = this.authStore;
    
    return (
      <>
        <GlobalStyles
          styles={{
            "*": {
              margin: 0,
              padding: 0,
              border: 0,
              fontWeight: 400,
            },
            "body": {
              height: '100%',
              fontFamily: themev5.typography.fontFamily,
              color: themev5.palette.colors.primary,
              overflow: 'hidden',
              backgroundColor: 'white',
        
              '& strong': {
                color: 'black',
                cursor: 'pointer',
                fontWeight: 500,
              },
        
              '& *': scrollStyles
            },
            "html": {
              height: '100%',
            },
            "#root": {
              height: '100%',
            },
            "a": {
              display: 'inline-flex',
              color: 'inherit',
              outline: 'none',
              textDecoration: 'none',
              cursor: 'pointer',
            },
            "span": {
              lineHeight: 1.2,
            },
            "input::-ms-clear": {
              display: 'none',
            },
          }}
        />
        <Typography component="div" className={classes.root}>
          <Route 
            path="/"
            component={loggedIn ? PrivateModule : PublicModule}  
          />
        </Typography>
      </>
    );
  }
}

const AppStyled = withStyles(
  App, 
  (theme, props) => ({
    "root": {
      height: '100vh',
    }
  })
);

export default withRouter(AppStyled);